import React, { useEffect, useState } from 'react';

interface AnimatedAvatarProps {
  src: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: () => void;
  isAnimating?: boolean;
  disableHoverEffect?: boolean;
  enabled?: boolean;
}

export const AnimatedAvatar: React.FC<AnimatedAvatarProps> = ({
  src,
  alt,
  size = 'md',
  onClick,
  isAnimating: externalAnimating,
  disableHoverEffect = false,
  enabled = true
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [internalAnimating, setInternalAnimating] = useState(false);
  
  // Use external animation state if provided, otherwise use internal state
  // If enabled is false, force isAnimating to be false
  const isAnimating = !enabled ? false : (externalAnimating !== undefined ? externalAnimating : internalAnimating);

  // Start animation on component mount if no external control is provided and enabled is true
  useEffect(() => {
    if (externalAnimating === undefined && enabled) {
      setInternalAnimating(true);
      const timer = setInterval(() => {
        setInternalAnimating(prev => !prev);
      }, 10000); // Toggle animation state every 10 seconds
      
      return () => clearInterval(timer);
    }
  }, [externalAnimating, enabled]);

  // Size classes
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
    xl: 'h-40 w-40'
  };

  return (
    <div
      className={`${sizeClasses[size]} relative ${onClick ? 'cursor-pointer' : ''}`}
      onMouseEnter={() => !disableHoverEffect && setIsHovered(true)}
      onMouseLeave={() => !disableHoverEffect && setIsHovered(false)}
      onClick={onClick}
    >
      {/* Outer glow effect */}
      <div className={`absolute inset-0 rounded-full blur-md bg-gradient-to-r from-blue-500 to-purple-500 opacity-50 ${isAnimating ? 'animate-pulse-fast' : ''}`}></div>
      
      {/* Rotating gradient border with color palette */}
      <div 
        className={`absolute inset-0 rounded-full ${isAnimating ? 'animate-spin-slow' : ''}`}
        style={{
          background: 'conic-gradient(from 0deg, var(--tw-gradient-from, #3b82f6), var(--tw-gradient-stops, #8b5cf6), var(--tw-gradient-to, #ec4899), var(--tw-gradient-from, #3b82f6))',
          animationDuration: '4s'
        }}
      ></div>
      
      {/* Pulsing inner border with color palette */}
      <div 
        className={`absolute inset-0.5 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 ${isAnimating ? 'animate-pulse-fast' : ''}`}
        style={{
          backgroundImage: isAnimating ? 'linear-gradient(135deg, var(--tw-gradient-from, #3b82f6), var(--tw-gradient-to, #8b5cf6))' : ''
        }}
      ></div>
      
      {/* Avatar container */}
      <div className="absolute inset-1 rounded-full overflow-hidden bg-white dark:bg-gray-800 p-0.5">
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover rounded-full"
        />
      </div>
      
      {/* Sparkle effects - show on hover or when animating */}
      {(isHovered || isAnimating) && (
        <>
          <div className="absolute top-0 right-0 h-2 w-2 bg-white rounded-full animate-ping" 
               style={{ animationDuration: '1.5s' }}></div>
          <div className="absolute bottom-1 left-1 h-1.5 w-1.5 bg-blue-300 rounded-full animate-ping" 
               style={{ animationDuration: '1.2s', animationDelay: '0.3s' }}></div>
          <div className="absolute top-1 left-2 h-1 w-1 bg-purple-300 rounded-full animate-ping" 
               style={{ animationDuration: '1s', animationDelay: '0.6s' }}></div>
        </>
      )}
    </div>
  );
};
